/**
 * Multi-Job Ganging & Sheet Balancer Utility
 * Optimizes slot assignment for 2 to 10 jobs on an imposition sheet
 * based on target quantities to achieve near-zero media waste.
 */

export interface GangingJob {
  id: string;
  clientName: string;
  jobName: string;
  width: number; // in pt
  height: number; // in pt
  requiredQty: number;
  color: string;
  assignedSlots?: number;
  templateData?: any;
}

export interface GangingBalanceResult {
  jobs: GangingJob[];
  totalSlots: number;
  sheetsNeeded: number;
  totalProduced: number;
  totalRequired: number;
  totalOverrun: number;
  efficiencyPercent: number;
  isValid?: boolean;
  errorMessage?: string;
  isOverflow?: boolean;
  overflowCount?: number;
}

/**
 * Calculates optimal slot allocation for each job on a sheet.
 */
export function calculateGangingBalance(
  rawJobs: GangingJob[],
  totalSlots: number
): GangingBalanceResult {
  if (!rawJobs || rawJobs.length === 0 || totalSlots <= 0) {
    return {
      jobs: [],
      totalSlots: Math.max(1, totalSlots),
      sheetsNeeded: 0,
      totalProduced: 0,
      totalRequired: 0,
      totalOverrun: 0,
      efficiencyPercent: 100,
      isValid: true,
      isOverflow: false,
      overflowCount: 0
    };
  }

  const jobs = rawJobs.map(j => ({ ...j }));
  const totalRequired = jobs.reduce((sum, j) => sum + (Number(j.requiredQty) || 1), 0);

  // Overflow Validation: When requested jobs exceed available grid slots
  if (jobs.length > totalSlots) {
    const overflowCount = jobs.length - totalSlots;
    const errorMsg = `Cannot gang ${jobs.length} jobs on a sheet with only ${totalSlots} grid slots. Need at least ${jobs.length} slots.`;
    return {
      jobs: jobs.map(j => ({ ...j, assignedSlots: 0 })),
      totalSlots,
      sheetsNeeded: 0,
      totalProduced: 0,
      totalRequired,
      totalOverrun: 0,
      efficiencyPercent: 0,
      isValid: false,
      isOverflow: true,
      overflowCount,
      errorMessage: errorMsg
    };
  }

  // 1. Initial proportional allocation based on quantities
  let allocatedSum = 0;
  const initialAllocations = jobs.map(j => {
    const ratio = (Number(j.requiredQty) || 1) / Math.max(1, totalRequired);
    const rawSlots = Math.round(ratio * totalSlots);
    const slots = Math.max(1, rawSlots); // Each job gets at least 1 slot
    allocatedSum += slots;
    return { job: j, ratio, slots };
  });

  // 2. Adjust sum to match exact totalSlots on the sheet grid
  let delta = totalSlots - allocatedSum;

  if (delta > 0) {
    // Distribute remaining slots to jobs with highest remainder / requiredQty
    initialAllocations.sort((a, b) => b.job.requiredQty - a.job.requiredQty);
    let idx = 0;
    while (delta > 0) {
      initialAllocations[idx % initialAllocations.length].slots++;
      delta--;
      idx++;
    }
  } else if (delta < 0) {
    // Reduce slots from jobs that have more than 1 slot
    initialAllocations.sort((a, b) => b.slots - a.slots);
    let idx = 0;
    while (delta < 0) {
      if (initialAllocations[idx % initialAllocations.length].slots > 1) {
        initialAllocations[idx % initialAllocations.length].slots--;
        delta++;
      }
      idx++;
      if (idx > initialAllocations.length * 10) break; // safeguard
    }
  }

  // 3. Calculate sheets needed and total overruns
  let maxSheetsNeeded = 1;
  for (const item of initialAllocations) {
    item.job.assignedSlots = item.slots;
    const sheets = Math.ceil(item.job.requiredQty / Math.max(1, item.slots));
    if (sheets > maxSheetsNeeded) {
      maxSheetsNeeded = sheets;
    }
  }

  let totalProduced = 0;
  let totalOverrun = 0;

  for (const item of initialAllocations) {
    const produced = maxSheetsNeeded * item.slots;
    const overrun = Math.max(0, produced - item.job.requiredQty);
    totalProduced += produced;
    totalOverrun += overrun;
  }

  const efficiencyPercent = totalProduced > 0
    ? Math.round(((totalProduced - totalOverrun) / totalProduced) * 1000) / 10
    : 100;

  return {
    jobs: initialAllocations.map(a => a.job),
    totalSlots,
    sheetsNeeded: maxSheetsNeeded,
    totalProduced,
    totalRequired,
    totalOverrun,
    efficiencyPercent,
    isValid: true,
    isOverflow: false,
    overflowCount: 0
  };
}

/**
 * Maps grid cell keys (r-c) to ganging job IDs according to slot allocations.
 */
export function mapGridSlotsToGangingJobs(
  jobs: GangingJob[],
  rows: number,
  cols: number,
  strategy: 'quantity_balanced' | 'row_partition' | 'grid_pack' = 'quantity_balanced',
  disabledSlots: string[] = []
): Record<string, GangingJob> {
  const slotMap: Record<string, GangingJob> = {};
  if (!jobs || jobs.length === 0 || rows <= 0 || cols <= 0) return slotMap;

  const totalCells = rows * cols;
  const enabledCellsCount = totalCells - (Array.isArray(disabledSlots) ? disabledSlots.length : 0);
  
  if (enabledCellsCount <= 0) return slotMap;

  const balanceResult = calculateGangingBalance(jobs, enabledCellsCount);

  if (balanceResult.isValid === false) {
    throw new Error(balanceResult.errorMessage || `Ganging overflow: ${jobs.length} jobs exceed ${enabledCellsCount} available slots.`);
  }

  const balancedJobs = balanceResult.jobs;

  // Flatten job pool according to assignedSlots count
  const slotPool: GangingJob[] = [];
  for (const job of balancedJobs) {
    const count = job.assignedSlots || 1;
    for (let i = 0; i < count; i++) {
      slotPool.push(job);
    }
  }

  // Fill up to enabledCellsCount
  while (slotPool.length < enabledCellsCount) {
    slotPool.push(balancedJobs[0] || jobs[0]);
  }

  let poolIdx = 0;

  // Fill row by row, skipping disabled slots
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const key = `${r}-${c}`;
      if (disabledSlots && Array.isArray(disabledSlots) && disabledSlots.includes(key)) {
        continue;
      }
      if (poolIdx < slotPool.length) {
        slotMap[key] = slotPool[poolIdx];
        poolIdx++;
      }
    }
  }

  return slotMap;
}

/**
 * Default sample multi-client ganging batch (3 clients)
 */
export const DEFAULT_GANGING_JOBS: GangingJob[] = [
  {
    id: 'job-client-a',
    clientName: 'Client A - Metro Organics',
    jobName: 'Oval Juice Label 50x50mm',
    width: 141.7, // 50mm in pt
    height: 141.7,
    requiredQty: 500,
    color: '#3B82F6' // Blue
  },
  {
    id: 'job-client-b',
    clientName: 'Client B - Apex Auto',
    jobName: 'Rect Warning Decal 75x50mm',
    width: 212.6, // 75mm in pt
    height: 141.7, // 50mm in pt
    requiredQty: 300,
    color: '#10B981' // Emerald
  },
  {
    id: 'job-client-c',
    clientName: 'Client C - Cyber Tech',
    jobName: 'Square Serial Tag 40x40mm',
    width: 113.4, // 40mm in pt
    height: 113.4,
    requiredQty: 200,
    color: '#A855F7' // Purple
  }
];
